import { useEffect, useRef, useState, useCallback } from 'react';
import { websocketService } from '@/services/websocket/websocket-service';
import { getFreshToken } from '@/utils/auth-token-provider';
import { NodeStatusEvent } from '@/types/workflow-types';

export const useWebSocket = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  const connect = useCallback(async () => {
    try {
      setConnectionError(null);

      // Get a fresh token at connection time (handles Auth0 refresh)
      const freshToken = await getFreshToken();

      if (!freshToken) {
        console.warn(
          '[useWebSocket] No token available for WebSocket connection',
        );
      }

      websocketService.setAuthToken(freshToken);

      await websocketService.connect();
      setIsConnected(true);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to connect to WebSocket';
      console.error('[useWebSocket] Connection failed:', {
        error: errorMessage,
        timestamp: new Date().toISOString(),
      });
      setConnectionError(errorMessage);
      setIsConnected(false);
    }
  }, []);

  const disconnect = useCallback(
    (reason: string = 'useWebSocket hook cleanup') => {
      websocketService.disconnect(reason);
      setIsConnected(false);
      setConnectionError(null);
    },
    [],
  );

  const subscribe = useCallback(
    (eventType: string, callback: (event: NodeStatusEvent) => void) => {
      // Clean up previous subscription if it exists
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }

      // Subscribe to new event type
      unsubscribeRef.current = websocketService.subscribe(eventType, callback);
    },
    [],
  );

  const subscribeToAll = useCallback(
    (callback: (event: NodeStatusEvent) => void) => {
      // Clean up previous subscription if it exists
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }

      // Subscribe to all events
      unsubscribeRef.current = websocketService.subscribe('*', callback);
    },
    [],
  );

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
      disconnect();
    };
  }, [disconnect]);

  // Sync connection status with actual WebSocket state
  useEffect(() => {
    const checkConnectionStatus = () => {
      const actualConnectionStatus = websocketService.isConnected();
      if (actualConnectionStatus !== isConnected) {
        console.log('[useWebSocket] Connection status changed:', {
          from: isConnected,
          to: actualConnectionStatus,
          timestamp: new Date().toISOString(),
        });
        setIsConnected(actualConnectionStatus);
        if (actualConnectionStatus) {
          setConnectionError(null);
        }
      }
    };

    // Check immediately
    checkConnectionStatus();

    // Set up interval to check connection status periodically
    const interval = setInterval(checkConnectionStatus, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [isConnected]);

  return {
    isConnected,
    connectionError,
    connect,
    disconnect,
    subscribe,
    subscribeToAll,
  };
};
